import { type ReactNode, Suspense, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';

import { idleClassOf, sectionRefusedAt } from '@/app/navigation';
import { CommandPalette, usePaletteShortcut } from '@/app/shell/command-palette';
import { Header } from '@/app/shell/header';
import { ScreenRefusal } from '@/app/shell/screen-refusal';
import { Sidebar } from '@/app/shell/sidebar';
import { Sheet, SheetContent, SheetTitle } from '@/design/components/sheet';
import { useIdleLock } from '@/features/auth/idle';
import { useSessionContext } from '@/features/auth/session';
import { SwitchOperatorDialog, useSignOut } from '@/features/auth/switch-operator';
import { useRealtimeStream } from '@/features/realtime/subscription';

/** The landmark the skip link jumps to. Exported so the link and the target cannot disagree. */
export const MAIN_CONTENT_ID = 'main-content';

/**
 * The application shell: a skip link, a section list, a header that always names the operator, the
 * command palette, the toast surface, and the route below.
 *
 * **Keyboard first, because the gate is worked at speed.** The first thing Tab reaches is the skip
 * link; the section list is a real list of links in visual order; the palette opens on Control-K or
 * Command-K from anywhere; every control has a visible focus ring from the token sheet, and none of
 * them removes it. Nothing here needs a pointer.
 *
 * **The idle window is a property of the route below, re-armed on every navigation.** `idleClassOf`
 * reads it from the section table, so walking from a gate screen to an administration one changes
 * the window from five minutes to sixty and starts it again. The lock it trips is an overlay, never
 * a navigation: the operator's half-filled form is still mounted underneath it.
 *
 * **The layout does not move when the theme changes.** Nothing below branches on the resolved
 * theme: light and dark are the same tree with different custom-property values, which is what lets
 * a token sheet be a design system rather than two stylesheets.
 *
 * **`print-chrome` marks what is the application rather than the content.** The evacuation
 * roll-call is printed from an administration screen, so the sidebar and the header are on the
 * sheet unless something says they are not; `design/tokens.css`'s `@media print` block drops them.
 * It is a class rather than a `print:hidden` utility per element because the rule belongs with the
 * page-break rules it works with, and because a printout is one decision rather than three.
 *
 * **The route below is rendered inside a `Suspense` boundary, and it is here rather than around
 * each screen.** The administration section is the application's one dynamic import, so its ten
 * screens all resolve one module (`features/admin/screens.ts`): a boundary per screen would be
 * eight boundaries that can never suspend once the first one has resolved. Putting it around the
 * `Outlet` also keeps the shell itself mounted while that request is in flight — the sidebar, the
 * header and the operator's own name stay on screen, exactly as they do while any other screen is
 * fetching its data, so the section opening never looks like a navigation to a blank page.
 */
export function AppShell(): ReactNode {
  const { t } = useTranslation();
  const { state, lock } = useSessionContext();
  const signOut = useSignOut();
  const location = useLocation();
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [switchingOperator, setSwitchingOperator] = useState(false);

  const openPalette = useCallback(() => {
    setPaletteOpen(true);
  }, []);
  // Only while somebody is actually at the terminal. See `usePaletteShortcut`.
  const unlocked = state.status === 'active';
  usePaletteShortcut(openPalette, unlocked);

  // Only an ACTIVE session idles out. A terminal that is already locked has nothing left to lock,
  // and one whose session has expired is showing a sign-in over the operator's work.
  useIdleLock({
    idle: idleClassOf(location.pathname),
    enabled: unlocked,
    onIdle: lock,
  });

  // **AD-57's stream, on the same predicate as the lock.** It is held open for as long as somebody
  // is at this terminal and dropped the moment they are not, which is what keeps a locked screen
  // from drawing anything behind its overlay and what makes the unlock produce a current picture
  // rather than a replay. `realtime.stream` is declared `idle: 'none'`, so holding it open is not
  // mistaken for somebody being here and the terminal still locks on its own clock.
  useRealtimeStream({ enabled: unlocked });

  if (state.status === 'anonymous') {
    throw new Error('AppShell was mounted with no session');
  }
  const { session } = state;
  const { role } = session.user;
  // **The section is guarded, not just its link**, for the reason `features/admin/admin-screen.tsx`
  // gives for its own screens: a typed `/scan` would otherwise mount the gate for an analyst and
  // let it fail request by request. The sidebar and the palette already hide the link; this is the
  // part that is a control.
  const refused = sectionRefusedAt(location.pathname, role);

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <a
        href={`#${MAIN_CONTENT_ID}`}
        className="sr-only rounded-md bg-accent px-4 py-2 text-accent-foreground focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50"
      >
        {t('shell.skipToContent')}
      </a>

      <div className="print-canvas grid min-h-dvh md:grid-cols-[16rem_1fr]">
        <div className="print-chrome hidden border-r bg-surface md:block">
          <Sidebar role={role} />
        </div>

        <Sheet open={navigationOpen} onOpenChange={setNavigationOpen}>
          <SheetContent side="left" className="w-64 p-0" closeLabel={t('shell.closeNavigation')}>
            <SheetTitle className="sr-only">{t('shell.navigationLabel')}</SheetTitle>
            <Sidebar
              role={role}
              onNavigate={() => {
                setNavigationOpen(false);
              }}
            />
          </SheetContent>
        </Sheet>

        <div className="flex min-w-0 flex-col">
          <Header
            session={session}
            onOpenNavigation={() => {
              setNavigationOpen(true);
            }}
            onOpenPalette={openPalette}
            onSwitchOperator={() => {
              setSwitchingOperator(true);
            }}
            onSignOut={signOut}
          />
          <main id={MAIN_CONTENT_ID} aria-label={t('shell.mainLabel')} className="flex-1 p-6">
            <Suspense
              fallback={
                <p className="text-sm text-muted-foreground" role="status">
                  {t('common.loading')}
                </p>
              }
            >
              {refused === null ? (
                <Outlet />
              ) : (
                <div className="flex flex-col gap-4">
                  <h1 className="text-2xl font-semibold tracking-tight">
                    {t(`nav.${refused.id}`)}
                  </h1>
                  <ScreenRefusal role={role} />
                </div>
              )}
            </Suspense>
          </main>
        </div>
      </div>

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        role={role}
        onSwitchOperator={() => {
          setSwitchingOperator(true);
        }}
        onSignOut={signOut}
      />
      <SwitchOperatorDialog open={switchingOperator} onOpenChange={setSwitchingOperator} />
    </div>
  );
}
